import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { OrderConflictError, updateOrderFields } from '../../lib/orders';
import { ORDER_STATUS_COLORS } from '../../lib/order-constants';

interface StatusEditPopoverProps {
  orderId: string;
  currentStatus: string;
  currentStatusLabel: string;
  availableStatuses: string[];
  expectedLastModifiedDate: string;
  canEdit: boolean;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

export function StatusEditPopover({
  orderId,
  currentStatus,
  currentStatusLabel,
  availableStatuses,
  expectedLastModifiedDate,
  canEdit,
  onSaved,
}: StatusEditPopoverProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const editable = canEdit && availableStatuses.length > 0;
  const badgeClass = clsx(
    'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium',
    ORDER_STATUS_COLORS[currentStatus] || 'bg-slate-100 text-slate-600'
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!editable) {
    // Op een geannuleerde order stuurt de server een lege `availableStatuses`
    // (zie api/orders/_status-whitelist.ts). De pil is dan bewust niet klikbaar;
    // de tooltip wijst naar het herstelpad zodat de klik-reflex niet doodloopt.
    const hint =
      currentStatus === 'Geannuleerd'
        ? 'Geannuleerde order — status is niet wijzigbaar. Herstel hem via de kaart onderaan deze pagina.'
        : undefined;
    return (
      <span className={badgeClass} title={hint}>
        {currentStatusLabel}
      </span>
    );
  }

  const select = async (status: string) => {
    if (status === currentStatus || pending) {
      setOpen(false);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await updateOrderFields(
        orderId,
        { newStatus: status },
        expectedLastModifiedDate
      );
      onSaved(result.nieuweLastModifiedDate);
      setOpen(false);
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Order is ondertussen gewijzigd. Vernieuwen…');
        window.dispatchEvent(new CustomEvent('order:conflict'));
      } else {
        setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        className={clsx(badgeClass, 'hover:ring-1 hover:ring-slate-300 disabled:opacity-60')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {pending && (
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
          </svg>
        )}
        <span>{currentStatusLabel}</span>
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-20 min-w-[14rem] bg-white border border-slate-200 rounded-lg shadow-lg py-1"
        >
          {/* Fout bovenaan en buiten de scroll-container: onderaan een lijst van
              19 items stond hij standaard buiten beeld, waardoor een geweigerde
              statuswijziging als "er gebeurt niets" overkwam. */}
          {error && (
            <p className="px-3 py-1.5 text-xs text-amber-700 border-b border-slate-100">⚠ {error}</p>
          )}
          <div className="max-h-80 overflow-y-auto">
          {availableStatuses.map((status) => {
            const isCurrent = status === currentStatus;
            return (
              <button
                key={status}
                type="button"
                role="menuitem"
                disabled={pending}
                onClick={() => select(status)}
                className={clsx(
                  'w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2',
                  isCurrent && 'bg-slate-50 font-medium'
                )}
              >
                <span
                  className={clsx(
                    'inline-block w-2 h-2 rounded-full',
                    (ORDER_STATUS_COLORS[status] || 'bg-slate-300').split(' ')[0]
                  )}
                />
                <span className="flex-1">{status}</span>
                {isCurrent && (
                  <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
          </div>
          <p className="px-3 py-1.5 text-[11px] text-slate-400 border-t border-slate-100">
            ⚠ Bij sommige statussen ontvangt de klant automatisch een e-mail.
          </p>
        </div>
      )}
    </div>
  );
}
